import React, { useState } from "react";
import { usePopper } from "react-popper";
import styled from "styled-components";
import { colors } from "../colors/color";
import { TextInput } from "./TextInput";

export type SuggesterTextInputProps = {
    text:string,
    visibleOptions:{identifier?:string, text:string}[],
    disabled?:boolean,
    onChange:(value:string)=>void,
    onFocus?:()=>void,
    onBlur?:(e: React.FocusEvent<HTMLInputElement>)=>void,
    onKeyDown?:(e: React.KeyboardEvent<HTMLInputElement>)=>void,
    onSelectOption:(selection:{identifier?:string, text:string})=>void,
    onEnterOnUnmarked?:()=>void
}
export function SuggesterTextInput(props:SuggesterTextInputProps): JSX.Element {
    const [focus, setFocus] = useState(false);
    const [markedIndex, setMarkedIndex] = useState<number|undefined>(undefined);
    const [inTab, setInTab] = useState<boolean>(false);

    const textInputRef = React.useRef(null);

    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        modifiers: [
            { name: 'arrow', options: { element: arrowElement } }],
        placement:'bottom-start'
    });

    function handleTextChange(value:string)
    {
        setMarkedIndex(undefined);
        props.onChange(value);
    }

    function handleFocus()
    {
        setFocus(true);
        props.onFocus?.();
    }

    function handleBlur(e: React.FocusEvent<HTMLInputElement>)
    {
        // Comment out to stay open and debug
        // ---------------
        if(inTab)
            setInTab(false);
        else{
            setFocus(false);
            setMarkedIndex(undefined); 
        }
        // ---------------
        props.onBlur?.(e);
    }

    function handleKeyDown(e:React.KeyboardEvent<HTMLInputElement>){
        if(e.key === 'ArrowDown'){
            if(markedIndex === undefined)
                setMarkedIndex(0);
            else if(markedIndex < props.visibleOptions.length-1)
                setMarkedIndex(markedIndex+1);
        }
        else if(e.key === 'ArrowUp'){
            if(markedIndex > 0)
                setMarkedIndex(markedIndex-1);
        }
        else if(e.key === 'Tab'){
            setInTab(true);
        }
        else if(e.key === 'Enter'){
            if(markedIndex !== undefined){
                props.onSelectOption(props.visibleOptions[markedIndex]);
                textInputRef.current.blur();
            }
            else{
                if(props.onEnterOnUnmarked)
                    props.onEnterOnUnmarked();
            }
        }

        props.onKeyDown?.(e);
    }

    function handleSelect(o:{identifier?:string, text:string})
    {
        setFocus(false);
        props.onSelectOption(o);
    }

    return (
        <>
            <span ref={setReferenceElement}>
                <TextInput
                    innerRef={textInputRef}
                    disabled={props.disabled}
                    value={props.text} 
                    onChange={handleTextChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown} 
                />
            </span>

            {focus && 
                <div ref={setPopperElement} style={{...styles.popper, zIndex:100000000}} {...attributes.popper} >
                    {focus && props.visibleOptions.length > 0 &&
                        <>
                            <StyledOptionPanel>
                                <ul>
                                    {props.visibleOptions.map((o,i) => 
                                        <ListItem key={i} index={i} option={o} isMarked={i === markedIndex} onSelect={handleSelect} />
                                    )}
                                </ul>
                            </StyledOptionPanel>
                            <div ref={setArrowElement} style={styles.arrow} />
                        </>
                    }
                </div>
            }
        </>
    );
}

type ListItemProps = {
    index:number,
    option:{identifier?:string, text:string},
    isMarked:boolean,
    onSelect:(option:{identifier?:string, text:string})=>void
}
function ListItem(props:ListItemProps):JSX.Element
{
    return(
        <StyledOption marked={props.isMarked}>
            {/* Using mousedown since it fires before blur */}
            <button onMouseDown={() => {
                props.onSelect(props.option)
            }}>
                {props.option.text}
            </button>
        </StyledOption>
    );
}

const StyledOptionPanel = styled.div`
    border:1px solid ${colors.suggester.optionList.border};
    border-radius: 4px;
    background: ${colors.suggester.optionList.background};
    padding:12px 0px;

    ul {
        list-style: none;
        margin:0;
        padding:0;
    }

    & button:focus-visible{
        background: ${colors.suggester.optionList.background};
    }
`;

const StyledOption = styled.li<{marked:boolean}>`
    margin:0;
    padding:0;
    button {
        border:none;
        background-color: ${props => props.marked ? colors.suggester.optionList.backgroundHover : colors.suggester.optionList.background};
        font-family: "Noto Sans",sans-serif;
        width: 100%;
        font-size: 16px;
        line-height: 18px;
        text-align: left;
        line-height: 24px;
        padding: 4px 20px;

        :hover{
            background-color:${colors.suggester.optionList.backgroundHover};
            cursor: pointer;
        }
    }
`;